import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useInventoryMode } from "@/hooks/useInventoryMode";
import { Clock, CheckCircle2 } from "lucide-react";
import logo from '../assets/images/logo-artie.png';

const ScorePendingPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  // In inventory mode no score is computed, so the five-second poll below — which waits for the
  // project to be marked reviewed — would never resolve. This page would sit there telling a creator
  // their score is being calculated, indefinitely. Leave first.
  const inventoryMode = useInventoryMode();

  // Check if we should show pending page or redirect to initial assessment
  const showPendingPage = import.meta.env.VITE_SHOW_PENDING_PAGE === 'true';

  useEffect(() => {
    if (inventoryMode) {
      navigate(
        projectId ? `/upload-thanks?projectId=${projectId}` : '/upload-thanks',
        { replace: true },
      );
    }
  }, [inventoryMode, projectId, navigate]);

  useEffect(() => {
    // null means the answer has not arrived; true means the redirect above is already running. Only
    // an explicit false should start polling for something that will actually happen.
    if (inventoryMode !== false) return;
    if (showPendingPage) {
      // If VITE_SHOW_PENDING_PAGE is true, redirect to initial assessment
      if (projectId) {
        navigate(`/initial-assessment?projectId=${projectId}`);
      }
    } else {
      // Otherwise, use the original polling logic
      let interval: NodeJS.Timeout;

      const checkStatus = async () => {
        if (!projectId) return;
        try {
          const project = await apiClient.getProject(projectId);

          // If the project IS reviewed (approved), we go to results.
          // Explicitly check for true to prevent false positives
          if (project.is_reviewed === true) {
            navigate(`/projects/${projectId}`);
          }
        } catch (err) {
          console.error("Error polling project status:", err);
        }
      };

      interval = setInterval(checkStatus, 5000); // Poll every 5 seconds

      // Initial check
      checkStatus();

      return () => clearInterval(interval);
    }
  }, [inventoryMode, projectId, navigate, showPendingPage]);

  // If redirecting to initial assessment, return null
  if (showPendingPage) {
    return null;
  }

  // Otherwise, show the original pending page
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12 text-center relative">
        {/* Back Button - Top Left */}
        <button
          onClick={() => navigate("/dashboard")}
          className="absolute top-6 left-6 text-slate-400 hover:text-[#F59E0B] transition-colors flex items-center gap-1.5 text-sm font-medium group cursor-pointer"
        >
          <span className="group-hover:-translate-x-0.5 transition-transform">←</span>
          Dashboard
        </button>

        <div className="w-24 h-24 mx-auto mb-8 relative mt-4">
          <div className="absolute inset-0 bg-indigo-100 rounded-full blur-xl opacity-50"></div>
          <img
            src={logo}
            alt="Artie"
            className="w-full h-full rounded-full object-cover border-4 border-white shadow-lg relative z-10"
          />
          <div className="absolute -right-2 -bottom-2 bg-white p-2 rounded-full shadow-md z-20">
            <Clock className="w-6 h-6 text-indigo-600" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-slate-900 mb-4 font-cabinet">
          Your Artie Score is being calculated
        </h1>

        <p className="text-lg text-slate-600 mb-8 max-w-lg mx-auto font-ibm leading-relaxed">
          Artie is analyzing your project details and script. Your comprehensive score and report will be ready within <span className="font-semibold text-slate-900">24 hours</span>.
        </p>

        <div className="bg-indigo-50 rounded-xl p-6 text-left max-w-md mx-auto border border-indigo-100">
          <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-indigo-600" />
            What happens next?
          </h3>
          <ul className="space-y-3 text-sm text-indigo-800/80">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
              Our team reviews your submission for quality and completeness.
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
              You'll receive an email notification once your score is ready.
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
              You can refresh this page to check the status.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ScorePendingPage;